// cSpell:ignore xyflow Silae SILAE
import { Handle, type NodeProps, type Node } from "@xyflow/react";
import { GitBranch } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { GraphNodeData } from "../utils/buildGraph";
import { effectiveConditionOperators } from "../utils/effectiveConditionOperators";
import { NodeShell } from "./NodeShell";
import { ConditionsListBlock } from "./parts/ConditionsListBlock";
import { useDiagramHandlePositions } from "../hooks/useDiagramHandlePositions";

/** Détermine la clé i18n de la description selon les opérateurs et la présence d'une branche else. */
function conditionsDescriptionKey(
  ops: string[],
  condCount: number,
  hasElse: boolean,
): string {
  const suffix = hasElse ? "WithElse" : "";
  if (condCount <= 1)
    return `step5Validation.conditions.descriptionSingle${suffix}`;
  const effective = effectiveConditionOperators(ops, condCount);
  const unique = new Set(effective.map((o) => o.toUpperCase()));
  if (unique.size > 1)
    return `step5Validation.conditions.descriptionMixed${suffix}`;
  const op = [...unique][0];
  return op === "OR"
    ? `step5Validation.conditions.descriptionAllOr${suffix}`
    : `step5Validation.conditions.descriptionAllAnd${suffix}`;
}

export function ConditionsNode({ data }: NodeProps<Node<GraphNodeData>>) {
  const { t } = useTranslation();
  const { source: sourcePos, isHorizontal } = useDiagramHandlePositions();
  const conditions = data.conditions ?? [];
  const ops = data.conditionsOperators ?? [];
  const hasElseBranch = data.hasElseBranch === true;
  const i18nKey = conditionsDescriptionKey(
    ops,
    conditions.length,
    hasElseBranch,
  );

  // En vertical  : deux points côte à côte en bas   (left: 50% ± 12px)
  // En horizontal: deux points l'un sur l'autre à droite (top: 50% ± 12px)
  const ifStyle = isHorizontal
    ? { top: "calc(50% - 12px)" }
    : { left: "calc(50% - 12px)" };
  const elseStyle = isHorizontal
    ? { top: "calc(50% + 12px)" }
    : { left: "calc(50% + 12px)" };

  return (
    <NodeShell
      variant="conditions"
      icon={GitBranch}
      title={t("step5Validation.conditions.title")}
      targetStep={data.targetStep}
      hideBottomHandle
    >
      <ConditionsListBlock
        conditions={conditions}
        conditionsOperators={ops}
        descriptionKey={i18nKey}
        descriptionComponents={{
          main: <span className="font-semibold" />,
          emerald: (
            <span className="font-semibold text-emerald-700 dark:text-emerald-400" />
          ),
          orange: (
            <span className="font-semibold text-orange-700 dark:text-orange-400" />
          ),
        }}
      />

      <Handle
        type="source"
        position={sourcePos}
        id="if"
        style={ifStyle}
        className="bg-emerald-500! w-2.5! h-2.5! border-2! border-white! dark:border-zinc-950! z-100"
      />
      {hasElseBranch && (
        <Handle
          type="source"
          position={sourcePos}
          id="else"
          style={elseStyle}
          className="bg-orange-500! w-2.5! h-2.5! border-2! border-white! dark:border-zinc-950! z-100"
        />
      )}
    </NodeShell>
  );
}
